/**
 * The settings menu, behind the cog next to your name.
 *
 * Travel mode, theme and hiding the interface each used to be its own button
 * in the top bar. None of them is something you touch while doing anything —
 * you pick a theme once, you pick walking once, you hide the interface to look
 * at the city — so they were spending permanent space on rare decisions, and
 * on a phone that space is the map.
 *
 * Travel mode lives here rather than in the route panel on purpose: it is a
 * preference about you, not a property of one journey, and asking for it again
 * every time you route somewhere is asking a question you already answered.
 */

import { setTheme, THEMES, themeName } from "../theme/palette.js";

const LABELS = { dedsec: "DedSec", gta: "GTA" };

/** Build a segmented control; returns a function that repaints its state. */
function segmented(host, options, current, onPick) {
  host.replaceChildren();
  const buttons = options.map(({ key, label }) => {
    const button = document.createElement("button");
    button.type = "button";
    button.textContent = label;
    button.dataset.key = key;
    button.addEventListener("click", () => onPick(key));
    host.append(button);
    return button;
  });

  return () => {
    const active = current();
    for (const button of buttons) {
      button.setAttribute("aria-pressed", String(button.dataset.key === active));
    }
  };
}

export function mountSettings({
  modes = [], getMode, setMode, setChromeHidden, chromeHidden, onChange,
} = {}) {
  const cog = document.getElementById("settings");
  const menu = document.getElementById("settings-menu");
  if (!cog || !menu) return { close() {}, refresh() {} };

  const paintModes = segmented(
    document.getElementById("settings-modes"),
    modes.map((m) => ({ key: m.key, label: m.label })),
    () => getMode?.(),
    (key) => { setMode?.(key); refresh(); onChange?.(); },
  );

  const paintThemes = segmented(
    document.getElementById("settings-themes"),
    Object.keys(THEMES).map((key) => ({ key, label: LABELS[key] ?? key })),
    () => themeName(),
    (key) => { setTheme(key); refresh(); },
  );

  const hideButton = document.getElementById("settings-hide-ui");

  function refresh() {
    paintModes();
    paintThemes();
    const hidden = Boolean(chromeHidden?.());
    hideButton?.setAttribute("aria-pressed", String(hidden));
    if (hideButton) hideButton.textContent = hidden ? "Show interface" : "Hide interface";
  }

  /**
   * Hang the menu under the cog, kept inside the viewport.
   *
   * Done here rather than in CSS because the top bar wraps at narrow widths,
   * so how far down the cog sits is not a number a stylesheet knows. The
   * phone layout pins the menu to the bottom edge instead and overrides this.
   */
  function place() {
    const rect = cog.getBoundingClientRect();
    const width = menu.offsetWidth || 240;
    const left = Math.min(
      Math.max(8, rect.right - width),
      window.innerWidth - width - 8,
    );
    menu.style.top = `${rect.bottom + 6}px`;
    menu.style.left = `${left}px`;
  }

  function open(next) {
    menu.hidden = !next;
    cog.setAttribute("aria-expanded", String(next));
    if (next) { refresh(); place(); }
  }

  window.addEventListener("resize", () => { if (!menu.hidden) place(); });

  cog.addEventListener("click", (event) => {
    event.stopPropagation();
    // The spin is removed on the way in as well as the way out: clicking again
    // before the animation ends would otherwise find the class already there
    // and play nothing.
    cog.classList.remove("spinning");
    void cog.offsetWidth;
    cog.classList.add("spinning");
    open(menu.hidden);
  });
  cog.addEventListener("animationend", () => cog.classList.remove("spinning"));

  hideButton?.addEventListener("click", () => {
    setChromeHidden?.(!chromeHidden?.());
    refresh();
    // Hiding the interface and leaving the menu covering it would be a joke.
    if (chromeHidden?.()) open(false);
  });

  // Clicking the map, or anything else, puts it away.
  document.addEventListener("click", (event) => {
    if (menu.hidden || menu.contains(event.target)) return;
    open(false);
  });
  window.addEventListener("keydown", (event) => {
    if (event.key === "Escape" && !menu.hidden) open(false);
  });


  /**
   * On desktop, MARK / SCAN / CAM live in here rather than in the top bar.
   *
   * The nodes are moved, not copied. They keep their ids and every listener
   * already bound to them, so nothing that reaches for document.getElementById
   * ("scan") — the keyboard shortcuts, the scan controller, the HUD — has to
   * know this happened. Cloning would have quietly given all of that a second
   * button that does nothing.
   *
   * Phones keep them where they are: the tools panel is already the place a
   * thumb goes, and burying them behind a cog would add a tap to the two
   * things people actually do on a phone.
   */
  function relocateActions() {
    const host = document.getElementById("settings-actions");
    const group = document.getElementById("settings-actions-group");
    const bar = document.querySelector(".topbar-tools");
    if (!host || !group || !bar) return;

    const narrow = window.matchMedia("(max-width: 820px)").matches;
    const ids = ["add-mark", "scan", "add-camera"];

    for (const id of ids) {
      const button = document.getElementById(id);
      if (!button) continue;
      const wanted = narrow ? bar : host;
      if (button.parentElement === wanted) continue;
      // Back into the bar in their original order, ahead of the sign-in button.
      if (narrow) bar.insertBefore(button, document.getElementById("login"));
      else host.append(button);
    }
    group.hidden = narrow;
  }

  relocateActions();
  window.addEventListener("resize", relocateActions);

  refresh();
  return { close: () => open(false), refresh };
}
